import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { downloadFileFromData } from "./src/other/utils/utils.jsx";
import { StringHelper } from "@bryntum/schedulerpro";

const createMenuStructure = (menuDefinition) => {
  const flattenMenu = (items, parentPath = '') => {
    return items.reduce((acc, item) => {
      const currentPath = parentPath ? `${parentPath}.${item.id}` : item.id;

      acc[currentPath] = {
        ...item,
        path: currentPath
      };

      if (item.children) {
        const childrenFlat = flattenMenu(item.children, currentPath);
        return { ...acc, ...childrenFlat };
      }

      return acc;
    }, {});
  };

  return {
    flatMenu: flattenMenu(menuDefinition),
    menuDefinition
  };
};

const CheatsUtility = ({ menuConfig }) => {
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [currentMenuPath, setCurrentMenuPath] = useState('main');

  // Memoized menu structure
  const { flatMenu } = useMemo(() =>
    createMenuStructure([
      {
        id: 'main',
        label: 'Swivu Dev Tools',
        children: menuConfig
      }
    ]), [menuConfig]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      /** event.metaKey = windows key */
      const isSlash = e.key === '/' || e.keyCode === 191;
      const isAltPressed = e.altKey;
      const isMetaPressed = e.metaKey;  // Command on macOS, Windows key on Win
      const isCtrlPressed = e.ctrlKey;

      // You want Alt (or Option on mac), but NOT Ctrl or Cmd
      if (isAltPressed && isSlash && !isMetaPressed && !isCtrlPressed) {
        e.preventDefault();
        setIsPopupVisible(prev => !prev);
        setCurrentMenuPath('main');
      }

      if (e.key === 'Escape') {
        // If popup is visible, handle escape
        if (isPopupVisible) {
          // If we're in a submenu, go back one level
          if (currentMenuPath !== 'main') {
            const parentPath = currentMenuPath.split('.').slice(0, -1).join('.');
            setCurrentMenuPath(parentPath || 'main');
          } else {
            // If in main menu, close the popup
            setIsPopupVisible(false);
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPopupVisible, currentMenuPath]);

  const renderMenu = () => {
    // Get current level menu items
    const currentLevelItems = Object.values(flatMenu)
      .filter(item => {
        const itemPathParts = item.path.split('.');
        const currentPathParts = currentMenuPath.split('.');

        return (
          itemPathParts.length === currentPathParts.length + 1 &&
          itemPathParts.slice(0, -1).join('.') === currentMenuPath
        );
      });

    const handleItemClick = (item) => {
      if (item.children) {
        // If item has children, navigate to submenu
        setCurrentMenuPath(item.path);
      } else if (item.action) {
        // If item has an action, execute it
        item.action();
        setIsPopupVisible(false);
      }
    };

    const handleCancel = () => {
      // If in a submenu, go back one level
      if (currentMenuPath !== 'main') {
        const parentPath = currentMenuPath.split('.').slice(0, -1).join('.');
        setCurrentMenuPath(parentPath || 'main');
      } else {
        // If in main menu, close the popup
        setIsPopupVisible(false);
      }
    };

    return (
      <div
        style={{
          position: 'fixed',
          color: "#333333",
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 11000
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            width: '300px',
            maxHeight: '80%',
            overflowY: 'auto'
          }}
        >
          <h2 style={{ marginBottom: '15px', textAlign: "center", marginTop: 0 }}>
            {flatMenu[currentMenuPath]?.label}
          </h2>
          {currentLevelItems.map((item, index) => (
            <button
              key={item.path}
              onClick={() => handleItemClick(item)}
              style={{
                display: 'block',
                width: '100%',
                padding: '10px',
                marginBottom: '10px',
                backgroundColor: item.children ? '#e0e0e0' : '#f0f0f0',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              {index + 1}. {item.label} {item.children ? '>' : ''}
            </button>
          ))}
          <button
            onClick={handleCancel}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: 'red',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {currentMenuPath === 'main' ? 'Cancel' : 'Back'}
          </button>
        </div>
      </div>
    );
  };

  return isPopupVisible ? createPortal(renderMenu(), document.body) : null;
};

/*
* Log States
*     Bryntum Project
*     Editor State
*     Record For Selected Row
*     Web Socket Instance
*     Auth User
* Import / Export
*     Import View // File Picker -> Import
*     Export View // Select View -> Export
*
* */

const LogIt = (message, data) => {
  console.log(`📌 [${message}]:`, data);
}

// Example Usage in App.js
export const SwivuDevTools = ({
  data: { bryntumContext, authContext }
}) => {
  const menuConfig = [
    {
      id: 'states',
      label: 'Log States',
      children: [
        {
          id: 'context',
          label: 'Bryntum Context',
          action: () => LogIt("Bryntum Context", bryntumContext),
        },
        {
          id: 'view-grids',
          label: 'View Grids',
          action: () => LogIt("View Grids", bryntumContext.viewManager.pageViewsRef),
        },
        {
          id: 'project-model',
          label: 'Project Model',
          action: () => LogIt("Project Model", bryntumContext.project),
        },
        {
          id: 'editor-state',
          label: 'Editor State',
          action: () => LogIt("Editor State", bryntumContext.editorHandler.editors),
        }
        ,
        {
          id: 'record-selected-row',
          label: 'Selected Row Record',
          action: () => {
            const grids = bryntumContext.viewManager.pageViewsRef;
            if (grids) {
              const gridsSelectedRecords = Object.entries(grids).reduce((accum, [gridName, grid]) => {
                accum[gridName] = grid.selectedRecords?.length > 1 ? grid.selectedRecords: grid.selectedRecord
                return accum;
              }, {});
              LogIt("Selected Row Record's", gridsSelectedRecords);
            }
          },
        },
        {
          id: 'ws-instance',
          label: 'Websocket Instance',
          action: () => LogIt("Websocket Instance", bryntumContext.swivuWebSocketRef?.current),
        },
        {
          id: 'auth-user',
          label: 'Auth User',
          action: () => LogIt("Auth User", authContext.user),
        },
        {
          id: "selected-board",
          label: "Selected Board",
          action: () => LogIt("Selected Board", bryntumContext.selectedBoard)
        },
        {
          id: "selected-view",
          label: "Selected View",
          action: () => {
            const selectedView = bryntumContext.viewManager.renderState.selectedView
            LogIt("View State from Local Storage", selectedView);
          }
        }
      ]
    },
    {
      id: 'import-export',
      label: 'Import / Export',
      children: [
        {
          id: 'import-view',
          label: 'Import View',
          action: () => {
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = '.json';
            fileInput.onchange = (event) => {
              const file = event.target.files[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                  try {
                    const view = StringHelper.safeJsonParse(e.target.result);
                    if (view) {
                      LogIt(`Import View - (${view.name})`, view);
                      delete view.id;
                      view.order = 0;
                      view.name = `📌 ${view.name}`;
                      const viewRecord = bryntumContext.project.viewsStore.createRecord(view)
                      bryntumContext.project.viewsStore.add(viewRecord);
                    } else {
                      alert("Invalid file not a desired view format");
                    }
                    // You can add your import logic here
                  } catch (error) {
                    console.error('Error parsing imported data:', error);
                  }
                };
                reader.readAsText(file);
              }
            };
            fileInput.click();
          }
        },
        {
          id: 'export-view',
          label: 'Export View',
          children: bryntumContext?.project?.viewsStore
            .query((view) => !view.isDefaultView, true).sort((a, b) => a.order - b.order)
            .map((view) => ({
              id: view.id,
              label: view.name,
              action: () => {
                LogIt(`Export View - (${view.name})`, view);
                downloadFileFromData(new Blob([JSON.stringify(view.data)]), `Exported View - ${view.name}`, "json");
              }
            }))
        }
      ]
    }
  ];

  return (
    <div>
      {/* Your app components */}
      <CheatsUtility menuConfig={menuConfig} />
    </div>
  );
};
